import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { addDays, format, startOfWeek } from "date-fns";
import { fetchHours } from "../../../api/hour";
import { intlLocaleFor } from "../../../i18n/languages";

export function weekDates(date) {
  const monday = startOfWeek(new Date(`${date}T12:00:00`), { weekStartsOn: 1 });
  return Array.from({ length: 7 }, (_, index) => format(addDays(monday, index), "yyyy-MM-dd"));
}

export async function fetchWeekHours(userId, from, to) {
  const hours = [];
  const perPage = 100;
  for (let page = 1; page <= 100; page += 1) {
    const response = await fetchHours({ date_from: from, date_to: to, filters: [{ name: "USER", value: userId }], view: "all", page, per_page: perPage });
    if (!Array.isArray(response?.data)) throw new Error("Invalid hours response");
    hours.push(...response.data);
    const totalCount = response.totalCount == null ? null : Number(response.totalCount);
    if (totalCount !== null && Number.isFinite(totalCount) && totalCount >= 0) {
      if (hours.length >= totalCount) return hours;
    } else if (response.data.length < perPage) {
      return hours;
    }
    if (!response.data.length || page >= 100) throw new Error("Incomplete hours response");
  }
  throw new Error("Incomplete hours response");
}

export function DashboardWeekHours({ userId, companyId, selectedDate, onSelectDate, refreshTick }) {
  const { t, i18n } = useTranslation();
  const dates = useMemo(() => weekDates(selectedDate), [selectedDate]);
  const from = dates[0];
  const to = dates[6];
  const [state, setState] = useState({ loading: true, hours: [], error: false });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, hours: [], error: false });
    if (userId) fetchWeekHours(userId, from, to).then((hours) => {
      if (!cancelled) setState({ loading: false, hours, error: false });
    }).catch(() => { if (!cancelled) setState({ loading: false, hours: [], error: true }); });
    return () => { cancelled = true; };
  }, [userId, companyId, from, to, refreshTick, retry]);
  const totals = dates.map((date) => state.hours.filter((hour) => String(hour.date).slice(0, 10) === date).reduce((sum, hour) => sum + (Number(String(hour.time ?? 0).replace(",", ".")) || 0), 0));
  const max = Math.max(1, ...totals);
  const locale = intlLocaleFor(i18n.language);
  return <div className="pd-week" aria-label={t("dashboardDesign.weekHours")}>
    {state.error ? <div role="alert" className="pd-load-error">{t("dashboardDesign.hoursError")} <button type="button" onClick={() => setRetry((v) => v + 1)}>{t("dashboardDesign.retry")}</button></div> : <>
      <div className="pd-week-days" aria-busy={state.loading}>{dates.map((date, index) => <button key={date} type="button" aria-pressed={selectedDate === date} onClick={() => onSelectDate(date)} aria-label={`${date}: ${state.loading ? t("dashboardDesign.loading") : totals[index].toFixed(1) + " t"}`}>
        <span className="pd-week-bar"><span style={{ height: `${(totals[index] / max) * 100}%` }} /></span>
        <span>{new Date(`${date}T12:00:00`).toLocaleDateString(locale, { weekday: "short" })}</span>
        <small>{state.loading ? "—" : `${totals[index].toFixed(1)} t`}</small>
      </button>)}</div>
      <p>{t("dashboardDesign.weekTotal", { hours: state.loading ? "—" : totals.reduce((sum, n) => sum + n, 0).toFixed(1) })}</p>
    </>}
  </div>;
}
